/**
 * InboxPreviewRouter — CENTER pane of the Inbox Cockpit.
 *
 * Switches on `selected.work_type` and mounts the matching detail renderer.
 * Replaces the inline empty-state branch in DealPreviewPane that previously
 * blocked every non-deal card type (article review, LinkedIn post, email
 * outreach, KB/scout/startup task) from rendering in the preview pane.
 *
 * The seven existing detail renderers are reused as-is — this file only
 * wires work_type → renderer and forwards the three action callbacks the
 * renderers consume (onArticleAction for articles, onAccept + onReject
 * for LinkedIn/deal-sourcing/KB/scout/email items).
 *
 * Authority: docs/superpowers/plans/2026-04-22-inbox-universal-routing-and-polish.md
 *            WI-INBOX-ROUTING-001 Stage 1.
 * Canonical mapping recovered from the deleted InboxDetailRenderer.jsx
 * DETAIL_RENDERERS object (commit fe62b916, Stage 2 of WI-UI-CLEANUP-001).
 */

import React from 'react';
import { DS_COLORS, DS_TYPOGRAPHY } from '@/config/ui-invariants';
import { COCKPIT_LABELS, WORK_TYPES } from '@/config/task-display-config';
import { Button } from '@/components/ui/button';

import DealPreviewPane from './DealPreviewPane';
import PreviewErrorBoundary from './PreviewErrorBoundary';
import { ArticleReviewDetail } from '../detailRenderers/ArticleReviewDetail';
import { LinkedInPostDetail } from '../detailRenderers/LinkedInPostDetail';
import { DealSourcingTaskDetail } from '../detailRenderers/DealSourcingTaskDetail';
import { EmailTaskDetail } from '../detailRenderers/EmailTaskDetail';
import { KBTaskDetail } from '../detailRenderers/KBTaskDetail';
import { ScoutTaskDetail } from '../detailRenderers/ScoutTaskDetail';
import { StartupTaskDetail } from '../detailRenderers/StartupTaskDetail';
import { GenericTaskDetail } from '../detailRenderers/GenericTaskDetail';
import { PrinciplePromotionDetail } from '../detailRenderers/PrinciplePromotionDetail';
import { StartupReviewDetail } from '../detailRenderers/StartupReviewDetail';

// ─── Canonical work_type → renderer mapping ───────────────────────────────────
//
// Every entry uses WORK_TYPES from task-display-config.js — never a raw string.
// When a new persisted work_type is introduced, extend WORK_TYPES first, then
// add the mapping here. This preserves the CLAUDE.md constants-authority rule.

const RENDERER_MAP = {
  [WORK_TYPES.ARTICLE_REVIEW]:      'ArticleReviewDetail',
  [WORK_TYPES.ARTICLE_ANALYSIS]:    'ArticleReviewDetail',
  [WORK_TYPES.LINKEDIN_POST]:       'LinkedInPostDetail',
  [WORK_TYPES.TRACXN_LIVEDEALS]:    'DealSourcingTaskDetail',
  [WORK_TYPES.FIRST_LOOK]:          'DealSourcingTaskDetail',   // R04: the generic first-look card, any source
  [WORK_TYPES.DEAL_SCREEN]:         'DealPreviewPane',
  [WORK_TYPES.CAMPAIGN]:            'EmailTaskDetail',
  [WORK_TYPES.EMAIL_OUTREACH]:      'EmailTaskDetail',
  [WORK_TYPES.LEAD_GEN_SCOUT]:      'ScoutTaskDetail',
  [WORK_TYPES.KB]:                  'KBTaskDetail',
  [WORK_TYPES.STARTUP]:             'StartupTaskDetail',
  [WORK_TYPES.REVIEW]:              'GenericTaskDetail',
  [WORK_TYPES.PRINCIPLE_PROMOTION]: 'PrinciplePromotionDetail',
  [WORK_TYPES.STARTUP_REVIEW]:      'StartupReviewDetail',
};

const COMPONENTS = {
  ArticleReviewDetail,
  LinkedInPostDetail,
  DealSourcingTaskDetail,
  DealPreviewPane,
  EmailTaskDetail,
  KBTaskDetail,
  ScoutTaskDetail,
  StartupTaskDetail,
  GenericTaskDetail,
  PrinciplePromotionDetail,
  StartupReviewDetail,
};

function resolveRenderer(workType) {
  const key = RENDERER_MAP[workType];
  return COMPONENTS[key] || COMPONENTS.GenericTaskDetail;
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      data-testid="inbox-preview-empty"
      style={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        height: '100%',
      }}
    >
      <p
        style={{
          ...DS_TYPOGRAPHY.bodyMd,
          color: DS_COLORS.text.muted,
          margin: 0,
          textAlign: 'center',
        }}
      >
        {COCKPIT_LABELS.emptyState.preview}
      </p>
    </div>
  );
}

// ─── Pane frame ───────────────────────────────────────────────────────────────
//
// deal_screen and tracxn_livedeals already render in full-width panes owned by
// DealPreviewPane / DealSourcingTaskDetail. Other detail renderers expect to
// live inside a scrollable content card — we wrap them in a padded container
// so they match the Stage 2 visual footprint.
//
// `onOpenInspector` (B1 fix, WI-NIGHT-CLOSE-UI-RESTORE-AND-AMNESTY-STAGGER-001
// Leg B): below 1280px the Inspector pane moves into a slide-over drawer
// (CockpitLayout.jsx). Before this fix, only DealPreviewPane (the deal_screen
// renderer) rendered a trigger to open that drawer — every other work_type
// routed through PreviewFrame had no way to reach the Inspector once it left
// the desktop row, which is the CEO's reported "rightmost panel disappears"
// regression. Rendering the trigger once, here, covers every renderer that
// passes through PreviewFrame instead of requiring each of the dozen detail
// renderers to wire it individually.
function PreviewFrame({ children, dense, onOpenInspector }) {
  return (
    <div
      data-testid="inbox-preview-pane"
      role="region"
      aria-label={COCKPIT_LABELS.aria.preview}
      style={{
        flex: 1,
        minWidth: '480px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        background: DS_COLORS.surface.white,
      }}
    >
      <div style={{ padding: dense ? '16px 24px' : '24px 24px 32px' }}>
        {onOpenInspector && (
          <Button
            variant="outline"
            onClick={onOpenInspector}
            data-testid="open-inspector-btn"
            style={{ minHeight: 44, marginBottom: 16 }}
          >
            {COCKPIT_LABELS.inspectorActions.openInspector}
          </Button>
        )}
        {children}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object|null}  props.selected          — currently selected InboxItem or null
 * @param {function}     props.onSelect          — (id: string|null) => void
 * @param {function}     props.onOpenInspector   — opens the inspector drawer (tablet/narrow)
 * @param {function=}    props.onArticleAction   — (articleId, action, reason, calibration) => Promise
 * @param {function=}    props.onAccept          — (itemId, workType, payload?) => Promise
 * @param {function=}    props.onReject          — (itemId, reasonCode, workType?) => Promise
 * @param {function=}    props.onRefresh         — refetches the inbox after a successful action
 * @param {function=}    props.onAnnounce        — (message: string) => void — relay to CockpitLayout's
 *   persistent, page-level aria-live region (Leg 5 follow-up, inbox announcer
 *   gap). Passed through to every detail renderer below — React ignores
 *   unknown props, so this is generic and safe even for renderers that don't
 *   consume it yet. DealCard (via DealSourcingTaskDetail) is the first
 *   consumer; any future renderer can adopt it the same way.
 */
export default function InboxPreviewRouter({
  selected,
  onSelect,
  onOpenInspector,
  onArticleAction,
  onAccept,
  onReject,
  onPassDeal,
  onContacted,
  onRefresh,
  onAnnounce,
}) {
  if (!selected) return <EmptyState />;

  const workType = selected.work_type;

  // Every branch below is wrapped so a card that cannot render says so in this
  // pane, by name, instead of taking the whole CEO Office down through the
  // app-level boundary. Keyed on the card id so one bad card does not poison
  // the pane for the next one. See PreviewErrorBoundary.jsx.
  const guard = (content) => (
    <PreviewErrorBoundary resetKey={selected.id} cardTitle={selected.title}>
      {content}
    </PreviewErrorBoundary>
  );

  // DEAL_SCREEN and TRACXN_LIVEDEALS render full-bleed within their own panes —
  // pass them through without the PreviewFrame wrapper. DEAL_SCREEN receives
  // onAccept/onReject so its action bar (Pass / Schedule Call) can fire the
  // canonical inbox accept/reject endpoints. (WI-INBOX-BUTTONS-001)
  if (workType === WORK_TYPES.DEAL_SCREEN) {
    return guard(
      <DealPreviewPane
        key={selected.id}
        selected={selected}
        onSelect={onSelect}
        onOpenInspector={onOpenInspector}
        onAccept={onAccept}
        onReject={onReject}
      />,
    );
  }

  if (workType === WORK_TYPES.TRACXN_LIVEDEALS || workType === WORK_TYPES.FIRST_LOOK) {
    return guard(
      <PreviewFrame onOpenInspector={onOpenInspector}>
        <DealSourcingTaskDetail
          key={selected.id}
          item={selected}
          onPassDeal={onPassDeal}
          onContacted={onContacted}
          onAccept={onAccept}
          onReject={onReject}
          onRefresh={onRefresh}
          onAnnounce={onAnnounce}
        />
      </PreviewFrame>,
    );
  }

  const Renderer = resolveRenderer(workType);

  // Renderers expect { item, onAccept, onReject, onArticleAction } — pass all
  // three; individual components use only what they need. React ignores
  // unknown props so this is safe. The key={selected.id} forces React to
  // unmount and remount the renderer when the selected card changes, so
  // useState initializers in detail components (e.g. LinkedInPostDetail's
  // currentText) re-evaluate against the new item instead of capturing the
  // first selection forever. Authority: docs/handoffs/INBOX-FIX-PLAN-001.md.
  return guard(
    <PreviewFrame onOpenInspector={onOpenInspector}>
      <Renderer
        key={selected.id}
        item={selected}
        onAccept={onAccept}
        onReject={onReject}
        onArticleAction={onArticleAction}
        onRefresh={onRefresh}
        onAnnounce={onAnnounce}
      />
    </PreviewFrame>,
  );
}

// Exported for unit tests so assertions can target the resolution logic
// independent of React mounting.
export { resolveRenderer, RENDERER_MAP };
